//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import { Alert } from '@components/atoms/alert/Alert';
import Icon from '@components/atoms/icon/Icon';
import { BodyErrorText } from '@components/atoms/text/Text';
import { Mixins } from '@theme/mixins';
import { useIsMessageMissingTCMessage } from '@uia/rights/hooks/useIsMessageMissingTCMessage';
import { useIsMessageMissingTCUrl } from '@uia/rights/hooks/useIsMessageMissingTCUrl';
import { useRightsRequestTCMessage } from '@uia/rights/hooks/useRightsRequestTCMessage';
import { useRightsRequestTCUrl } from '@uia/rights/hooks/useRightsRequestTCUrl';
import { isDefined } from '@utils/filterPredicates';
import {
  hasHashtag,
  hasTagUsPhrase,
} from '@utils/social-text-utils/socialTextUtils';

export interface RequestErrorMessagesProps {
  isMissingHistoricalTCUrl: boolean;
  isReRequestRequiredForMissingTCUrl: boolean;
  isTikTokContent: boolean;
  rightsRequestText: string | undefined;
}

export const RequestErrorMessages = ({
  isMissingHistoricalTCUrl,
  isReRequestRequiredForMissingTCUrl,
  isTikTokContent,
  rightsRequestText,
}: RequestErrorMessagesProps) => {
  const rightsRequestTCMessage = useRightsRequestTCMessage({
    rightsRequestText,
  });

  const rightsRequestTCUrl = useRightsRequestTCUrl();

  const isMismatchingTCMessage =
    useIsMessageMissingTCMessage(rightsRequestText);

  const isMismatchingTCUrl = useIsMessageMissingTCUrl(rightsRequestText);

  const isMessageMissingHashtag = !hasHashtag(rightsRequestText);

  const isMessageMissingTagUs =
    isTikTokContent &&
    isDefined(rightsRequestText) &&
    !hasTagUsPhrase(rightsRequestText);

  return (
    <>
      {!!isMissingHistoricalTCUrl && (
        <ErrorText>
          <Icon name='error' />
          <span>
            You need to configure your Terms and Conditions URL. Go to the{' '}
            <ErrorRouteAnchor state='account-settings.rights' target='_blank'>
              Rights Requests Settings
            </ErrorRouteAnchor>{' '}
            page and choose Configure Messaging before requesting rights.
          </span>
        </ErrorText>
      )}
      {isMismatchingTCMessage && (
        <ErrorText>
          <Icon name='error' />
          <span>
            The rights request must contain the disclaimer statement&nbsp;
            <ErrorBoldText>{rightsRequestTCMessage}</ErrorBoldText>
          </span>
        </ErrorText>
      )}
      {isMessageMissingHashtag && (
        <ErrorText>
          <Icon name='error' />
          <span>Your rights request message must contain a hashtag.</span>
        </ErrorText>
      )}
      {isMessageMissingTagUs && (
        <ErrorText>
          <Icon name='error' />
          <span>
            The rights request message must contain the phrase “tag us” so the
            creator knows to tag the brand during approval.
          </span>
        </ErrorText>
      )}
      {isMismatchingTCUrl &&
        (isReRequestRequiredForMissingTCUrl ? (
          <Alert type='warning'>
            Content rights must be requested with the required hashtag and terms
            link to be eligible for distribution to the Visual Syndication
            Network.
          </Alert>
        ) : (
          <ErrorText>
            <Icon name='error' />
            <span>
              The rights request must contain your Terms and Conditions
              URL&nbsp;
              <ErrorBoldText>{rightsRequestTCUrl}</ErrorBoldText>
            </span>
          </ErrorText>
        ))}
    </>
  );
};

const ErrorText = styled(BodyErrorText)(
  ({ theme }) => `
    display: flex;
    justify-content: start;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);

// these are non-standard styles
const ErrorRouteAnchor = styled(PrimaryRouteAnchor)(
  ({ theme }) => `
  &&& {
    color ${theme.colors.aliases.error};
    text-decoration: underline;
  }
`,
);

const ErrorBoldText = styled.span(
  ({ theme }) => `
  ${Mixins.Text.BodyBold}
  & {
    color: ${theme.colors.aliases.error};  
  }
`,
);
